import { useCurrentPlan } from '@/hooks/useCurrentPlan';
import { UpsellModal } from '@/components/upsell/UpsellModal';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Lock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FEATURE_LABELS } from '@/config/plans';

/**
 * Page-level guard for plan-gated routes. When the workspace lacks the
 * required feature flag, this component renders a full-page lock screen
 * with the existing UpsellModal opened immediately — so direct URL
 * access (e.g. typing /employees on Starter) cannot bypass the sidebar
 * lock.
 *
 * Usage:
 *   <FeatureGuard feature="multi_user">
 *     <EmployeesListPage />
 *   </FeatureGuard>
 */
export function FeatureGuard({ feature, children }) {
  const { isLocked } = useCurrentPlan();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const tf = (fr, en) => (language === 'fr' ? fr : en);

  if (!feature || !isLocked(feature)) {
    return children;
  }

  const featureLabel = FEATURE_LABELS[feature]?.[language] || feature;

  return (
    <div
      className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-6"
      data-testid={`feature-guard-${feature}`}
    >
      <div className="max-w-md text-center space-y-4">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-600">
          <Lock className="h-6 w-6" />
        </div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">
          {tf('Fonctionnalité verrouillée', 'Locked feature')}
        </h1>
        <p className="text-muted-foreground">
          {tf(
            `"${featureLabel}" n'est pas inclus dans votre forfait actuel. Mettez à niveau pour débloquer ce module.`,
            `"${featureLabel}" is not included in your current plan. Upgrade to unlock this module.`
          )}
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            data-testid={`feature-guard-back-${feature}`}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {tf('Retour au tableau de bord', 'Back to dashboard')}
          </Button>
        </div>
      </div>

      {/* Auto-opened upsell modal — the user has to dismiss it or upgrade. */}
      <UpsellModal
        open
        onOpenChange={(open) => {
          if (!open) navigate('/dashboard');
        }}
        feature={feature}
      />
    </div>
  );
}

export default FeatureGuard;
